import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ApiClient, { fmtCurrency, shortId } from "../lib/api";
import { TID } from "../lib/testIds";
import Coin from "../components/Coin";
import { Section, KV, Tag } from "../components/Atoms";

export default function GamePage({ config }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const launchToken = params.get("launch_token");

  const [session, setSession] = useState(null);
  const [balance, setBalance] = useState(null);
  const [side, setSide] = useState("KARA");
  const [bet, setBet] = useState(100);
  const [phase, setPhase] = useState("idle"); // idle | flipping | settled
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);
  const [working, setWorking] = useState(false);

  const currency = session?.currency || config?.default_currency || "GBP";
  const playerId = session?.player_id;

  useEffect(() => {
    if (!launchToken) {
      nav("/launch", { replace: true });
      return;
    }
    (async () => {
      try {
        const s = await ApiClient.sessionValidate(launchToken);
        if (!s.ok) {
          toast.error(`Session invalid: ${s.error || s.status_code}`);
          nav("/launch");
          return;
        }
        setSession(s);
        const b = await ApiClient.balance(s.player_id, s.currency);
        setBalance(b);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
        nav("/launch");
      }
    })();
  }, [launchToken, nav]);

  async function refreshBalance() {
    if (!playerId) return;
    const b = await ApiClient.balance(playerId, currency);
    setBalance(b);
  }

  async function refreshHistory() {
    try {
      const h = await ApiClient.history(20);
      setHistory(h);
    } catch (e) {
      console.warn("history load failed", e);
    }
  }

  useEffect(() => {
    refreshHistory();
  }, [last]);

  async function flip() {
    if (!playerId) return;
    if (!bet || Number(bet) <= 0) {
      toast.error("Enter a valid bet");
      return;
    }
    if (balance && Number(bet) > Number(balance.balance || 0)) {
      toast.error("Insufficient balance");
      return;
    }
    setWorking(true);
    setPhase("idle");
    try {
      // Step 1: debit happens server-side before animation
      const res = await ApiClient.play({
        player_id: playerId,
        currency,
        side,
        bet_amount: Number(bet),
      });

      if (!res.ok) {
        toast.error(
          `Debit failed (${res?.debit?.error || res?.debit?.status_code || "unknown"})`
        );
        setWorking(false);
        return;
      }

      setLast(res);
      setPhase("flipping");
      // animation ~1.8s, then settle
      setTimeout(async () => {
        setPhase("settled");
        await refreshBalance();
        if (res.won) toast.success(`Win · ${fmtCurrency(res.win_amount, currency)}`);
        else toast.message("Round closed · no credit");
        setWorking(false);
      }, 1850);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setWorking(false);
    }
  }

  const lastResult = last?.result;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8 fade-in" data-testid={TID.gameRoot}>
      {/* MAIN STAGE */}
      <div className="space-y-6">
        <Section
          title="Round"
          right={
            <div className="flex items-center gap-2">
              <Tag tone="gold">
                Payout <strong data-testid={TID.payoutInfo}>{config?.payout_multiplier}x</strong>
              </Tag>
              <Tag tone="muted">Mode {config?.mode}</Tag>
            </div>
          }
        >
          <Coin phase={phase} result={lastResult} selectedSide={side} />

          <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto">
            <button
              type="button"
              className="side-pill py-4 font-display tracking-[0.25em]"
              data-active={side === "KARA"}
              data-testid={TID.selectKara}
              onClick={() => setSide("KARA")}
              disabled={working}
            >
              KARA
              <div className="text-[10px] tracking-[0.3em] text-[var(--kk-muted)] mt-1">HEADS</div>
            </button>
            <button
              type="button"
              className="side-pill py-4 font-display tracking-[0.25em]"
              data-active={side === "KRUS"}
              data-testid={TID.selectKrus}
              onClick={() => setSide("KRUS")}
              disabled={working}
            >
              KRUS
              <div className="text-[10px] tracking-[0.3em] text-[var(--kk-muted)] mt-1">TAILS</div>
            </button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-end gap-3 max-w-md mx-auto">
            <label className="flex-1 w-full">
              <span className="block text-[10.5px] uppercase tracking-[0.22em] text-[var(--kk-muted)] mb-1.5">
                Bet Amount ({currency})
              </span>
              <input
                data-testid={TID.betAmount}
                className="w-full bg-[var(--kk-graphite-2)] border border-[var(--kk-line)] text-[var(--kk-ivory)] font-mono px-3 py-2.5 rounded-sm focus:outline-none focus:border-[var(--kk-gold)]"
                type="number"
                min="1"
                step="1"
                value={bet}
                onChange={(e) => setBet(e.target.value)}
                disabled={working}
              />
            </label>
            <button
              type="button"
              data-testid={TID.flipBtn}
              className="btn-gold px-7 py-3 w-full sm:w-auto"
              onClick={flip}
              disabled={working || !playerId}
            >
              {working ? "Flipping…" : "Flip Coin"}
            </button>
          </div>

          {last && phase === "settled" && (
            <div className="mt-6 flex items-center justify-center gap-3 fade-in">
              <Tag tone={last.won ? "win" : "loss"}>
                Result · <strong data-testid={TID.resultValue}>{last.result}</strong>
              </Tag>
              <Tag tone="muted">Selected · {last.selected_side}</Tag>
              {last.won ? (
                <Tag tone="win">+ {fmtCurrency(last.win_amount, currency)}</Tag>
              ) : (
                <Tag tone="loss">− {fmtCurrency(last.bet_amount, currency)}</Tag>
              )}
            </div>
          )}
        </Section>

        <Section title="Round Detail">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <KV label="Round ID" value={last?.round_id} testid={TID.roundIdValue} />
            <KV label="Last Debit TXN" value={shortId(last?.debit?.txn_id, 14, 8)} testid={TID.lastDebitTxn} />
            <KV label="Debit Idempotency Key" value={shortId(last?.debit?.idempotency_key, 14, 8)} testid={TID.debitIdem} />
            <KV label="Last Credit TXN" value={shortId(last?.credit?.txn_id, 14, 8)} testid={TID.lastCreditTxn} />
            <KV label="Credit Idempotency Key" value={shortId(last?.credit?.idempotency_key, 14, 8)} testid={TID.creditIdem} />
            <KV label="Provider" value={config?.provider_id} testid={TID.providerIdValue} />
            <KV label="Game" value={config?.game_id} testid={TID.gameIdValue} />
            <KV label="Currency" value={currency} />
          </div>
          {last && (
            <details className="mt-4">
              <summary className="text-[11px] uppercase tracking-[0.22em] text-[var(--kk-muted)] cursor-pointer hover:text-[var(--kk-gold-soft)]">
                Raw transaction payloads
              </summary>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-[var(--kk-muted)] uppercase tracking-[0.2em] mb-1">Debit</div>
                  <pre className="code-block">{JSON.stringify(last.debit, null, 2)}</pre>
                </div>
                <div>
                  <div className="text-[10px] text-[var(--kk-muted)] uppercase tracking-[0.2em] mb-1">Credit</div>
                  <pre className="code-block">{JSON.stringify(last.credit, null, 2)}</pre>
                </div>
              </div>
            </details>
          )}
        </Section>

        <Section title="Recent Rounds" right={<span className="text-[10px] text-[var(--kk-muted)] font-mono uppercase">last 20</span>}>
          <div className="overflow-x-auto">
            <table className="kk-table" data-testid={TID.historyTable}>
              <thead>
                <tr>
                  <th>Round</th><th>Side</th><th>Result</th><th>Bet</th><th>Win</th><th>Balance After</th><th>Debit TXN</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-[var(--kk-muted)] py-6">No rounds yet</td></tr>
                )}
                {history.map((h) => (
                  <tr key={h.round_id}>
                    <td>{shortId(h.round_id, 10, 6)}</td>
                    <td>{h.selected_side}</td>
                    <td>
                      <span className={h.won ? "text-[#aee0a8]" : "text-[#e2a09d]"}>{h.result}</span>
                    </td>
                    <td>{fmtCurrency(h.bet_amount, h.currency)}</td>
                    <td>{h.won ? fmtCurrency(h.win_amount, h.currency) : "—"}</td>
                    <td>{fmtCurrency(h.balance_after, h.currency)}</td>
                    <td>{shortId(h?.debit?.txn_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* SIDEBAR */}
      <aside className="space-y-6">
        <Section title="Wallet">
          <div className="text-center py-3">
            <div className="text-[10.5px] uppercase tracking-[0.25em] text-[var(--kk-muted)]">
              Live Balance
            </div>
            <div
              data-testid={TID.balanceValue}
              className="font-display text-4xl text-[var(--kk-gold-soft)] mt-1 tracking-wider"
            >
              {balance ? fmtCurrency(balance.balance, balance.currency) : "—"}
            </div>
            <button
              type="button"
              onClick={refreshBalance}
              data-testid={TID.refreshBalance}
              className="btn-ghost-gold mt-3 px-3 py-1.5 text-[10px]"
            >
              Refresh from CoreBridge
            </button>
          </div>
        </Section>

        <Section title="Session">
          <KV label="Player ID" value={playerId} testid={TID.playerIdValue} />
          <KV label="Session ID" value={shortId(session?.session_id, 12, 8)} testid={TID.sessionIdValue} />
          <KV label="Launch Token" value={shortId(launchToken, 10, 6)} testid={TID.launchTokenValue} />
          <KV label="Operator" value={session?.operator_id} />
        </Section>

        <Section title="Provider">
          <KV label="Provider ID" value={config?.provider_id} />
          <KV label="Provider Name" value={config?.provider_name} mono={false} />
          <KV label="Game ID" value={config?.game_id} />
          <KV label="Game Name" value={config?.game_name} mono={false} />
        </Section>

        <p className="text-[10.5px] text-[var(--kk-muted)] leading-relaxed">
          Balance is never computed locally. Every value above is fetched from
          <span className="font-mono text-[var(--kk-gold-soft)]"> GET /operator/player/balance</span>
          after each CoreBridge call.
        </p>
      </aside>
    </div>
  );
}
